import {
  Checkbox,
  Css,
  GridColumn,
  GridDataRow,
  IconButton,
  SelectToggle,
  Tag,
  TagType,
  actionColumn,
  column,
  emptyCell,
  selectColumn,
  simpleHeader,
  useTestIds,
} from "@homebound/beam";
import { dateCell } from "src/components/gridTableCells";
import { ReadyPlanStatus, SaveProductOfferingInput } from "src/generated/graphqlTypes";
import { disableBasedOnPotentialOperationText } from "src/routes/components/PotentialOperationsUtils";
import { PLAN_FALLBACK_IMG } from "src/routes/libraries/plan-package/takeoffs/utils";
import { PackageVersionStatus } from "src/routes/libraries/product-offerings/overview/PackageVersionStatus";
import { ProductOfferingsOverviewTab_ProductOfferingFragment } from "src/routes/libraries/product-offerings/overview/ProductOfferingsOverviewTab.generated";
import { createProjectProductOfferingUrl } from "src/RouteUrls";

type HeaderRow = { kind: "header"; data: undefined };
type ProductOfferingRow = {
  kind: "productOffering";
  data: ProductOfferingsOverviewTab_ProductOfferingFragment;
  id: string;
};
export type ProductOfferingsOverviewTableRow = HeaderRow | ProductOfferingRow;

type CreateProductOfferingsOverviewColumnsOpts = {
  projectId: string;
  /** Auto-saves a toggle; the mutation returns the toggled fields so the row updates from the cache. */
  onToggle: (id: string, input: Pick<SaveProductOfferingInput, "showInCheckout" | "publishToUnderwriting">) => void;
};

export function createProductOfferingsOverviewColumns(
  opts: CreateProductOfferingsOverviewColumnsOpts,
): GridColumn<ProductOfferingsOverviewTableRow>[] {
  const { projectId, onToggle } = opts;
  return [
    selectColumn<ProductOfferingsOverviewTableRow>({
      // Disabled checkboxes explain why, like the Publish button on the product offering page
      productOffering: (po, { row }) => ({
        content: <SelectToggle id={row.id} disabled={publishDisabledReason(po)} />,
      }),
    }),
    column<ProductOfferingsOverviewTableRow>({
      id: "name",
      name: "Product offering",
      header: "Product offering",
      serverSideSortKey: "name",
      mw: "240px",
      productOffering: (po) => ({
        content: () => (
          <div css={Css.df.aic.gap1.mw0.$}>
            <img
              src={po.coverAsset?.previewUrl ?? PLAN_FALLBACK_IMG}
              alt=""
              css={Css.wPx(40).hPx(30).fs0.br4.objectCover.$}
            />
            <div css={Css.df.fdc.mw0.$}>
              <div css={Css.xsSb.truncate.$}>{po.name}</div>
              <div css={Css.xs.gray700.truncate.$}>{po.type.name}</div>
            </div>
          </div>
        ),
        value: po.name,
      }),
    }),
    column<ProductOfferingsOverviewTableRow>({
      id: "status",
      name: "Status",
      header: "Status",
      // TODO: Add serverSideSortKey once the BE supports it: https://linear.app/homebound-team/issue/PLAN-2009/febe-add-table-sort-by-status-and-last-promoted
      mw: "120px",
      productOffering: (po) => ({
        content: () => <Tag {...productOfferingStatusTag[po.effectiveStatus.code]} />,
        value: productOfferingStatusTag[po.effectiveStatus.code].text,
      }),
    }),
    column<ProductOfferingsOverviewTableRow>({
      id: "planPackage",
      name: "Plan package",
      header: "Plan package",
      mw: "140px",
      productOffering: (po) => ({
        content: () => (
          <PackageVersionCell
            name={po.planPackage.name}
            version={po.planPackageVersion.displayVersion}
            // PP publishes are synced into the POF automatically, so the PP dot is always green.
            hasUpdates={false}
            data-testid="planPackageVersion"
          />
        ),
        value: po.planPackage.name,
      }),
    }),
    column<ProductOfferingsOverviewTableRow>({
      id: "interior",
      name: "Design package: Interior",
      header: () => <HeaderWithSubHeader header="Design package" subHeader="Interior" />,
      mw: "140px",
      productOffering: (po) => ({
        content: () => (
          <PackageVersionCell
            name={po.interiorDesignPackage.name}
            version={po.interiorDesignPackageVersion.displayVersion}
            hasUpdates={po.interiorDesignPackage.availableUpdates.nonEmpty}
            data-testid="interiorVersion"
          />
        ),
        value: po.interiorDesignPackage.name,
      }),
    }),
    column<ProductOfferingsOverviewTableRow>({
      id: "exterior",
      name: "Design package: Exterior",
      header: () => <HeaderWithSubHeader header="Design package" subHeader="Exterior" />,
      mw: "140px",
      productOffering: (po) => ({
        content: () => (
          <PackageVersionCell
            name={po.exteriorDesignPackage.name}
            version={po.exteriorDesignPackageVersion.displayVersion}
            hasUpdates={po.exteriorDesignPackage.availableUpdates.nonEmpty}
            data-testid="exteriorVersion"
          />
        ),
        value: po.exteriorDesignPackage.name,
      }),
    }),
    column<ProductOfferingsOverviewTableRow>({
      id: "showInCheckout",
      name: "Show in Checkout",
      header: "Show in Checkout",
      align: "center",
      mw: "100px",
      // Don't open the side pane when toggling the checkbox
      wrapAction: false,
      productOffering: (po) => ({
        content: () => (
          <Checkbox
            label="Show in Checkout"
            checkboxOnly
            selected={po.showInCheckout}
            onChange={(showInCheckout) => onToggle(po.id, { showInCheckout })}
          />
        ),
        value: po.showInCheckout,
      }),
    }),
    column<ProductOfferingsOverviewTableRow>({
      id: "publishToUnderwriting",
      name: "Publish to UW",
      header: "Publish to UW",
      align: "center",
      mw: "100px",
      // Don't open the side pane when toggling the checkbox
      wrapAction: false,
      productOffering: (po) => ({
        content: () => (
          <Checkbox
            label="Publish to UW"
            checkboxOnly
            selected={po.publishToUnderwriting}
            disabled={disableBasedOnPotentialOperationText(po.canPublishToUnderwriting)}
            onChange={(publishToUnderwriting) => onToggle(po.id, { publishToUnderwriting })}
          />
        ),
        value: po.publishToUnderwriting,
      }),
    }),
    column<ProductOfferingsOverviewTableRow>({
      id: "updatedAt",
      name: "Last updated",
      header: "Last updated",
      serverSideSortKey: "updatedAt",
      mw: "110px",
      productOffering: (po) => dateCell(po.updatedAt),
    }),
    column<ProductOfferingsOverviewTableRow>({
      id: "promotedAt",
      name: "Last promoted",
      header: "Last promoted",
      // TODO: Add serverSideSortKey once the BE supports it: https://linear.app/homebound-team/issue/PLAN-2009/febe-add-table-sort-by-status-and-last-promoted
      mw: "110px",
      productOffering: (po) => dateCell(po.aggregateCheckout?.promotedAt),
    }),
    column<ProductOfferingsOverviewTableRow>({
      id: "promotedVersion",
      name: "Promoted version",
      header: "Promoted version",
      mw: "100px",
      productOffering: (po) => po.aggregateCheckout?.displayVersion ?? emptyCell,
    }),
    column<ProductOfferingsOverviewTableRow>({
      id: "currentVersion",
      name: "Current version",
      header: "Current version",
      mw: "100px",
      productOffering: (po) => po.aggregateLatest.displayVersion,
    }),
    actionColumn<ProductOfferingsOverviewTableRow>({
      id: "open",
      name: "Open",
      canHide: false,
      header: emptyCell,
      w: "48px",
      productOffering: (po) => (
        <IconButton
          icon="arrowFromLeft"
          label="Open product offering"
          onClick={createProjectProductOfferingUrl(projectId, po.id, po.aggregateLatest.id)}
          data-testid="openProductOffering"
        />
      ),
    }),
  ];
}

export function createProductOfferingsOverviewRows(
  productOfferings: ProductOfferingsOverviewTab_ProductOfferingFragment[],
): GridDataRow<ProductOfferingsOverviewTableRow>[] {
  return [
    simpleHeader,
    ...productOfferings.map((po) => ({
      kind: "productOffering" as const,
      data: po,
      id: po.id,
      // Selection only feeds bulk publish, so rows that can't be published can't be selected
      ...(publishDisabledReason(po) ? { selectable: false as const } : {}),
    })),
  ];
}

/** Why the offering can't be published, or `false` if it can. */
function publishDisabledReason(po: ProductOfferingsOverviewTab_ProductOfferingFragment): string | boolean {
  // `canPublish` only exists on the draft, so with no draft there's nothing to publish
  return po.aggregateDraft
    ? disableBasedOnPotentialOperationText(po.aggregateDraft.canPublish)
    : "The latest version has already been published";
}

export const productOfferingStatusTag: Record<ReadyPlanStatus, { text: string; type: TagType }> = {
  [ReadyPlanStatus.Draft]: { text: "In Draft", type: "neutral" },
  [ReadyPlanStatus.Active]: { text: "Published", type: "success" },
  [ReadyPlanStatus.Archived]: { text: "Archived", type: "warning" },
};

type HeaderWithSubHeaderProps = { header: string; subHeader: string };

function HeaderWithSubHeader(props: HeaderWithSubHeaderProps) {
  const { header, subHeader } = props;
  return (
    <div>
      <div>{header}</div>
      <div css={Css.xs.gray700.$}>{subHeader}</div>
    </div>
  );
}

type PackageVersionCellProps = { name: string; version: string; hasUpdates: boolean };

/** Package name over its current version and update status. */
function PackageVersionCell(props: PackageVersionCellProps) {
  const { name, version, hasUpdates } = props;
  const tid = useTestIds(props);
  return (
    <div css={Css.df.fdc.mw0.xs.$}>
      <div css={Css.truncate.$}>{name}</div>
      <PackageVersionStatus version={version} hasUpdates={hasUpdates} {...tid} />
    </div>
  );
}
